import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { PageHeader, Select, Skeleton } from '@solid/ui';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  CheckSquare,
  Search,
  CheckCircle2,
  List as ListIcon,
  Columns3,
} from 'lucide-react';
import { tasksApi } from '@/lib/api';
import { EmptyState } from '@/components/ui/EmptyState';
import { TaskItem } from '@/components/tasks/TaskItem';
import { TaskDetailModal } from '@/components/modals/TaskDetailModal';
import { DuplicateTaskModal } from '@/components/modals/DuplicateTaskModal';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { ConsolidatedBoard } from '@/components/tasks/ConsolidatedBoard';
import { useAuthStore } from '@/stores/auth.store';

type FilterTab = 'assigned' | 'all' | 'overdue' | 'completed';

export function Tasks() {
  const { user } = useAuthStore();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilter, setActiveFilter] = useState<FilterTab>('assigned');
  // The view is in the URL so a board someone is looking at is a link they can
  // send, and so the back button leaves it.
  const view = searchParams.get('view') === 'board' ? 'board' : 'list';
  const assigneeFilter = searchParams.get('assignee') ?? 'all';

  const setParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (!value) next.delete(key);
    else next.set(key, value);
    setSearchParams(next);
  };
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [duplicatingTask, setDuplicatingTask] = useState<any | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Fetch all tasks across all accessible projects
  const { data: tasksData, isLoading } = useQuery({
    queryKey: ['allTasks'],
    queryFn: () => tasksApi.getAllTasks(),
  });

  const allTasks = (tasksData || []).map((task: any) => ({
    ...task,
    taskListId: task.list.id,
    taskListName: task.list.name,
    projectId: task.list.project.id,
    projectName: task.list.project.name,
    organizationId: task.list.project.organizationId,
    projectMembers: [],
  }));
  
  // Toggle task mutation
  const toggleMutation = useMutation({
    mutationFn: (taskId: string) => tasksApi.toggleComplete(taskId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });

  // Update task status (for In Progress toggle)
  const updateStatusMutation = useMutation({
    mutationFn: ({ taskId, status }: { taskId: string; status: string }) =>
      tasksApi.updateTask(taskId, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
    },
  });

  // Delete task mutation
  const deleteTaskMutation = useMutation({
    mutationFn: (id: string) => tasksApi.deleteTask(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['allTasks'] });
      setDeleteConfirmId(null);
    },
    onError: (error) => {
      console.error('Failed to delete task:', error);
      setDeleteConfirmId(null);
    },
  });

  // Filter tasks
  const filteredTasks = allTasks.filter((task) => {
    // Search filter
    if (searchQuery && !task.title.toLowerCase().includes(searchQuery.toLowerCase())) {
      return false;
    }
    
    // Tab filter.
    //
    // Every tab except Completed is about OUTSTANDING work, and each tab's
    // count already said so — but the list did not. "Assigned to Me" counted
    // `status !== 'COMPLETED'` and then rendered every task you had ever
    // finished, sorted to the bottom, so the badge read 5 above a list of 40.
    // Finished work has a tab of its own; it does not belong in the others.
    switch (activeFilter) {
      case 'assigned':
        return task.assigneeId === user?.id && task.status !== 'COMPLETED';
      case 'overdue':
        return task.status !== 'COMPLETED' && task.dueDate && new Date(task.dueDate) < new Date();
      case 'completed':
        return task.status === 'COMPLETED';
      default:
        return task.status !== 'COMPLETED';
    }
  });
  
  // Sort by due date and priority
  const sortedTasks = [...filteredTasks].sort((a, b) => {
    // Completed tasks at the end
    if (a.status === 'COMPLETED' && b.status !== 'COMPLETED') return 1;
    if (b.status === 'COMPLETED' && a.status !== 'COMPLETED') return -1;
    
    // Overdue tasks first
    const aOverdue = a.dueDate && new Date(a.dueDate) < new Date();
    const bOverdue = b.dueDate && new Date(b.dueDate) < new Date();
    if (aOverdue && !bOverdue) return -1;
    if (bOverdue && !aOverdue) return 1;
    
    // Then by due date
    if (a.dueDate && b.dueDate) {
      return new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime();
    }
    
    return 0;
  });
  
  /**
   * Everyone with work in view, for the assignee filter. Derived from the tasks
   * themselves rather than fetched: the only people worth offering are the ones
   * who actually appear on this board, and a full directory would list dozens
   * who have nothing here.
   */
  const people = (() => {
    const seen = new Map<string, { id: string; name: string }>();
    for (const t of allTasks) {
      for (const p of (t.assignees?.length ? t.assignees : t.assignee ? [t.assignee] : [])) {
        if (p?.id && !seen.has(p.id)) seen.set(p.id, { id: p.id, name: p.name || 'Unknown' });
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
  })();

  const matchesAssignee = (task: any) => {
    if (assigneeFilter === 'all') return true;
    if (assigneeFilter === 'unassigned') return !task.assigneeId && !task.assignees?.length;
    return (
      task.assigneeId === assigneeFilter ||
      task.assignees?.some((a: any) => a.id === assigneeFilter)
    );
  };

  // The BOARD shows status columns, so it must not also be filtered by status —
  // the tabs would empty the very columns the board exists to show. It takes the
  // search and the assignee filter only.
  const boardTasks = allTasks.filter(
    (t: any) =>
      matchesAssignee(t) &&
      (!searchQuery || t.title.toLowerCase().includes(searchQuery.toLowerCase())),
  );

  const filterTabs: { key: FilterTab; label: string; count?: number }[] = [
    { key: 'assigned', label: 'Assigned to Me', count: allTasks.filter(t => t.assigneeId === user?.id && t.status !== 'COMPLETED').length },
    { key: 'all', label: 'All Tasks', count: allTasks.filter(t => t.status !== 'COMPLETED').length },
    { key: 'overdue', label: 'Overdue', count: allTasks.filter(t => t.status !== 'COMPLETED' && t.dueDate && new Date(t.dueDate) < new Date()).length },
    { key: 'completed', label: 'Completed', count: allTasks.filter(t => t.status === 'COMPLETED').length },
  ];
  
  if (isLoading) {
    return (
      <div aria-busy>
        <Skeleton className="mb-8 h-10 w-48" />
        <div className="space-y-4">
          {[1, 2, 3, 4, 5].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="w-full">
      <PageHeader
        title="Tasks"
        subtitle="All tasks across your projects"
        actions={
          <div className="flex items-center gap-2 rounded-lg bg-grey-100 px-3 py-1.5 text-sm">
            <CheckCircle2 className="w-4 h-4 text-success" />
            <span className="text-grey-600">
              {allTasks.length - allTasks.filter(t => t.status === 'COMPLETED').length} left - {allTasks.filter(t => t.status === 'COMPLETED').length} done
            </span>
          </div>
        }
      />
      
      {/* Search and Filters */}
      <div className="flex flex-col gap-4 mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-grey-400" />
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="input pl-10"
          />
        </div>
        
        <div className="flex flex-wrap items-center gap-3">
          <div className="w-48">
            <Select
              selectSize="sm"
              aria-label="Filter by assignee"
              data-testid="tasks-assignee-filter"
              options={[
                { value: 'all', label: 'Everyone' },
                ...(user?.id ? [{ value: user.id, label: 'Assigned to me' }] : []),
                { value: 'unassigned', label: 'Unassigned' },
                ...people.filter((p) => p.id !== user?.id).map((p) => ({ value: p.id, label: p.name })),
              ]}
              value={assigneeFilter}
              onChange={(e) => setParam('assignee', e.target.value === 'all' ? null : e.target.value)}
            />
          </div>

          <div className="flex-1" />

          <div className="flex items-center rounded-lg bg-grey-100 p-1">
            {([
              { key: 'list', icon: ListIcon, label: 'List view' },
              { key: 'board', icon: Columns3, label: 'Board view' },
            ] as const).map((v) => (
              <button
                key={v.key}
                onClick={() => setParam('view', v.key === 'list' ? null : v.key)}
                aria-label={v.label}
                aria-pressed={view === v.key}
                data-testid={`tasks-view-${v.key}`}
                className={cn(
                  'rounded-md p-2 transition-colors',
                  view === v.key ? 'bg-white text-secondary shadow-sm' : 'text-grey-500 hover:text-secondary',
                )}
              >
                <v.icon className="h-4 w-4" />
              </button>
            ))}
          </div>
        </div>

        {view === 'list' && (
        // min-w-0: without it a flex item refuses to shrink below its content,
        // so overflow-x-auto never engages and the ROW widens the page instead
        // of scrolling inside itself (mobile audit, 2026-08-27).
        <div className="flex min-w-0 items-center gap-2 overflow-x-auto pb-2">
          {filterTabs.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveFilter(tab.key)}
              className={cn(
                'flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors whitespace-nowrap',
                activeFilter === tab.key
                  ? 'bg-secondary text-white'
                  : 'bg-grey-100 text-grey-600 hover:bg-grey-200'
              )}
            >
              {tab.label}
              {tab.count !== undefined && tab.count > 0 && (
                <span className={cn(
                  'px-1.5 py-0.5 rounded-full text-xs',
                  activeFilter === tab.key
                    ? 'bg-white/20'
                    : 'bg-grey-200'
                )}>
                  {tab.count}
                </span>
              )}
            </button>
          ))}
        </div>
        )}
      </div>

      {view === 'board' ? (
        <ConsolidatedBoard tasks={boardTasks} onOpenTask={setSelectedTask} />
      ) : sortedTasks.length === 0 ? (
        <EmptyState
          icon={CheckSquare}
          title="No tasks found"
          description={
            searchQuery
              ? "Try adjusting your search"
              : activeFilter !== 'all'
                ? "No tasks match this filter"
                : "You don't have any tasks yet"
          }
        />
      ) : (
        <div className="space-y-2">
          {sortedTasks.map((task) => {
            const isInProgress = task.status === 'IN_PROGRESS';

            return (
              <TaskItem
                key={task.id}
                task={task}
                currentUserId={user?.id}
                onClick={() => setSelectedTask(task)}
                onToggleComplete={() => toggleMutation.mutate(task.id)}
                onToggleInProgress={() => updateStatusMutation.mutate({
                  taskId: task.id,
                  status: isInProgress ? 'PENDING' : 'IN_PROGRESS',
                })}
                onEdit={() => setSelectedTask(task)}
                onDuplicate={() => setDuplicatingTask(task)}
                isToggling={toggleMutation.isPending}
                isUpdatingStatus={updateStatusMutation.isPending}
                showProjectInfo
                compact
              />
            );
          })}
        </div>
      )}

      {/* Task Detail Modal */}
      {selectedTask && (
        <TaskDetailModal
          isOpen={!!selectedTask}
          onClose={() => setSelectedTask(null)}
          onDuplicate={() => {
            setDuplicatingTask(selectedTask);
            setSelectedTask(null);
          }}
          task={selectedTask}
          projectId={selectedTask.projectId}
          listId={selectedTask.taskListId}
          organizationId={selectedTask.organizationId}
        />
      )}

      {/* Delete confirmation modal */}
      {deleteConfirmId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-secondary/60 backdrop-blur-sm" onClick={() => setDeleteConfirmId(null)} />
          <div className="relative bg-white rounded-xl shadow-elevated p-6 max-w-sm w-full">
            <h3 className="text-heading-4 text-secondary mb-2">Delete Task?</h3>
            <p className="text-grey-500 text-sm mb-6">
              This action cannot be undone.
            </p>
            <div className="flex justify-end gap-3">
              <Button variant="ghost" onClick={() => setDeleteConfirmId(null)}>
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={() => deleteTaskMutation.mutate(deleteConfirmId)}
                isLoading={deleteTaskMutation.isPending}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Duplicate Task Modal */}
      {duplicatingTask && (
        <DuplicateTaskModal
          isOpen={!!duplicatingTask}
          onClose={() => setDuplicatingTask(null)}
          task={duplicatingTask}
          projectId={duplicatingTask.projectId}
          currentListId={duplicatingTask.taskListId}
          taskLists={[{ id: duplicatingTask.taskListId, name: duplicatingTask.taskListName || 'Current List' }]}
        />
      )}
    </div>
  );
}
